import { MOTION, RADIUS } from "@elyxeer/ui";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { type ChangeEvent, useRef, useState } from "react";

/**
 * The one-time code, drawn as one box per digit.
 *
 * A single transparent input covers the boxes so paste, autofill, correction and the
 * numeric keypad remain native browser behaviour. The boxes only render its value.
 */

interface CodeFieldProps {
  value: string;
  onChange: (value: string) => void;
  /** How many digits the code has. The API config tells us; it is not ours to assume. */
  length: number;
  /** The accessible name: the boxes visually replace a conventional field label. */
  label: string;
  error?: boolean;
  autoFocus?: boolean;
  testId?: string;
}

/** px — matches the height of a filled `TextField`. */
const BOX_HEIGHT = 56;

/** px — the caret is sized against the digit rather than the box. */
const CARET_WIDTH = 2;
const CARET_HEIGHT = 26;

export function CodeField({
  value,
  onChange,
  length,
  label,
  error = false,
  autoFocus = false,
  testId,
}: CodeFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [focused, setFocused] = useState(false);

  /** Digits only and never more than fit, so a pasted `123 456` still lands. */
  const handleChange = (event: ChangeEvent<HTMLInputElement>) =>
    onChange(event.target.value.replace(/\D/g, "").slice(0, length));

  /**
   * The boxes fill left to right. If a tap moves the real caret into the middle, put it
   * back at the end so the hidden input and visible rendering cannot disagree.
   */
  const pinCaret = () => {
    const input = inputRef.current;
    if (!input) return;
    const end = input.value.length;
    if (input.selectionStart !== end || input.selectionEnd !== end)
      input.setSelectionRange(end, end);
  };

  const caretAt = focused && value.length < length ? value.length : -1;
  const activeAt = focused ? Math.min(value.length, length - 1) : -1;

  return (
    <Box sx={{ position: "relative", display: "flex", gap: 1 }}>
      {Array.from({ length }, (_, index) => (
        <Box
          key={index}
          sx={{
            position: "relative",
            flex: 1,
            minWidth: 0,
            height: `${BOX_HEIGHT}px`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: `${RADIUS.md}px`,
            backgroundColor: "background.default",
            border: "1px solid",
            borderColor: error
              ? "error.main"
              : index === activeAt
                ? "primary.main"
                : "divider",
            transition: `border-color ${MOTION.transition}`,
          }}
        >
          <Typography
            component="span"
            variant="H2"
            sx={{ color: "text.primary" }}
          >
            {value[index] ?? ""}
          </Typography>

          {index === caretAt ? (
            <Box
              sx={{
                position: "absolute",
                width: `${CARET_WIDTH}px`,
                height: `${CARET_HEIGHT}px`,
                borderRadius: `${CARET_WIDTH / 2}px`,
                backgroundColor: "primary.main",
                "@keyframes elyxeer-caret-blink": {
                  "0%, 100%": { opacity: 1 },
                  "50%": { opacity: 0 },
                },
                animation: "elyxeer-caret-blink 1.1s step-end infinite",
              }}
            />
          ) : null}
        </Box>
      ))}

      <Box
        component="input"
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleChange}
        onSelect={pinCaret}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        inputMode="numeric"
        autoComplete="one-time-code"
        autoFocus={autoFocus}
        aria-label={label}
        data-testid={testId}
        sx={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          margin: 0,
          padding: 0,
          border: 0,
          outline: "none",
          background: "transparent",
          color: "transparent",
          caretColor: "transparent",
          textAlign: "center",
          // iOS zooms the viewport when a focused input is smaller than 16px.
          fontSize: "16px",
          fontFamily: "inherit",
        }}
      />
    </Box>
  );
}
